import { Box } from '@factorialco/deprecated-design-system'

import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Attachments from 'modules/bookkeepers/components/IncidenceInformation/Attachments'
import type IncidenceChanges from 'modules/bookkeepers/store/collections/changes'
import type { Files } from 'modules/bookkeepers/store/collections/files'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import {
  NewDetail,
  OldDetail,
  getOldIncidenceDetail,
} from 'modules/payroll_bundle/pages/IncidenceDetailPage/_ContractInfo/helper'

import LeaveInfoLine from './LeaveInfoLine'
import EauStatusContainer from './EauStatusContainer'
import hasFeature from 'modules/core/lib/hasFeature'

interface LeaveInfoProps {
  incidenceType: string
  incidenceDetailModel: IncidenceDetail
  incidenceFilesCollection: Files
  incidenceChangesCollection: IncidenceChanges
  canRequestEau: boolean
  application: string
}

const LeaveInfo = ({
  incidenceType,
  incidenceDetailModel,
  incidenceFilesCollection,
  incidenceChangesCollection,
  canRequestEau,
  application,
}: LeaveInfoProps) => {
  const hoursCount = parseFloat(incidenceDetailModel.attributes.hours)
  const isTypeInHours = !!hoursCount
  const newIncidenceDetail: NewDetail = incidenceDetailModel.toJS()
  const oldIncidenceDetail: OldDetail = getOldIncidenceDetail(incidenceChangesCollection)
  const termination_date = (date?: string) => {
    if (!date) return i18n.t('bookkeepers.incidence.details.no_end_date')
    return dateBackendToUI(date)
  }

  const isSickLeave = incidenceType === 'sick'
  const canCreateEauRequest = hasFeature('timeoff_electronic_sick_leave') && canRequestEau

  const detailTypeValue = (value: string | undefined) => {
    if (isTypeInHours) return i18n.tp('bookkeepers.incidence.details.hours', { count: hoursCount })
    if (value) return i18n.t('bookkeepers.incidence.details.half_day')
    return i18n.t('bookkeepers.incidence.details.full_day')
  }

  return (
    <Box gap="s16">
      {isSickLeave && hasFeature('timeoff_electronic_sick_leave') && (
        <EauStatusContainer
          incidenceId={incidenceDetailModel.attributes.incidenceId}
          leaveId={incidenceDetailModel.attributes.id}
          employeeId={incidenceDetailModel.attributes.employee_id}
          canRequestEau={canCreateEauRequest}
          application={application}
        />
      )}
      {newIncidenceDetail.start_on && (
        <LeaveInfoLine
          key={'start_on'}
          label={i18n.t('bookkeepers.incidence.details.starts_on')}
          oldValue={
            oldIncidenceDetail?.start_on ? dateBackendToUI(oldIncidenceDetail?.start_on) : undefined
          }
          newValue={dateBackendToUI(newIncidenceDetail.start_on)}
        />
      )}
      {newIncidenceDetail.finish_on && (
        <LeaveInfoLine
          key={'finish_on'}
          label={i18n.t('bookkeepers.incidence.details.ends_on')}
          oldValue={
            oldIncidenceDetail?.finish_on
              ? termination_date(oldIncidenceDetail?.finish_on)
              : undefined
          }
          newValue={termination_date(newIncidenceDetail.finish_on)}
        />
      )}
      {(newIncidenceDetail.half_day || isTypeInHours) && (
        <LeaveInfoLine
          key={'half_day'}
          label={i18n.t('bookkeepers.incidence.details.type')}
          oldValue={detailTypeValue(oldIncidenceDetail?.half_day)}
          newValue={detailTypeValue(newIncidenceDetail.half_day)}
        />
      )}
      {newIncidenceDetail?.description && (
        <LeaveInfoLine
          key={'description'}
          label={i18n.t('bookkeepers.incidence.details.description')}
          oldValue={oldIncidenceDetail?.description}
          newValue={newIncidenceDetail.description}
        />
      )}
      {incidenceFilesCollection.length > 0 && (
        <Attachments
          label={i18n.t('bookkeepers.incidence.details.attachments')}
          files={incidenceFilesCollection.models}
        />
      )}
    </Box>
  )
}

export default LeaveInfo
